'use client'

import { useState, useEffect } from 'react'
import styles from './TerminalTabs.module.css'
import { ZerodhaConnector } from '../ZerodhaConnector'

/**
 * @uiComponent
 * @name Holdings
 * @description The Holdings component displays the user's stock portfolio on the Zerodha platform.
 * It shows detailed information about each holding including quantity, average price, current price,
 * and profit/loss calculations. Provides real-time portfolio analytics and performance metrics
 * for equity investments with simplified interface design.
 * @location Holdings tab in the trading terminal
 * @prop apiKey The API key for Zerodha connector authentication
 * @prop apiSecret The API secret for Zerodha connector authentication
 * @prop accessToken Optional access token for enhanced authentication
 */

/**
 * @uiWorkflow
 * @name View Stock Portfolio on Zerodha
 * @description How to monitor your stock holdings and portfolio performance
 * @platform zerodha
 * @step 1 Navigate to the trading terminal [component: TerminalTabs] [action: navigate]
 * @step 2 Click on the "Holdings" tab to view your portfolio [component: Holdings] [action: click]
 * @step 3 Review your total portfolio value in the summary section [component: Holdings] [action: observe]
 * @step 4 Check your overall profit/loss for the entire portfolio [component: Holdings] [action: observe] [notes: Green indicates profit, red indicates loss]
 * @step 5 Examine individual stock holdings in the table [component: Holdings] [action: scroll]
 * @step 6 Compare average purchase price with current market price [component: Holdings] [action: observe]
 * @step 7 Monitor real-time P&L changes and percentage returns [component: Holdings] [action: observe]
 * @step 8 Use the data to make informed trading decisions [component: Holdings] [action: analyze]
 */

/**
 * @uiWorkflow
 * @name Analyze Individual Stock Performance in Zerodha Holdings
 * @description How to evaluate specific stock performance within your portfolio
 * @platform zerodha
 * @step 1 Open the Holdings tab in the trading terminal [component: Holdings] [action: click]
 * @step 2 Locate the specific stock in your holdings table [component: Holdings] [action: scroll]
 * @step 3 Compare the average price you paid with current market price [component: Holdings] [action: observe]
 * @step 4 Review the absolute P&L amount for that stock [component: Holdings] [action: observe]
 * @step 5 Check the percentage return on your investment [component: Holdings] [action: observe]
 * @step 6 Calculate the current value of your position [component: Holdings] [action: observe] [notes: Quantity × Current Price]
 * @step 7 Determine if you want to add more or sell based on performance [component: Holdings] [action: analyze]
 * @step 8 Consider portfolio allocation and rebalancing needs [component: Holdings] [action: analyze]
 */

interface ZerodhaHolding {
  asset: string
  free: string
  locked: string
  averagePrice: number
  lastPrice: number
  pnl: number
}

export default function Holdings({ apiKey, apiSecret, accessToken }: { apiKey: string, apiSecret: string, accessToken?: string }) {
  const [holdings, setHoldings] = useState<ZerodhaHolding[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchHoldings = async () => {
      if (!apiKey || !apiSecret) return
      
      setIsLoading(true)
      setError(null)
      
      try {
        // Create an instance of ZerodhaConnector
        const connector = new ZerodhaConnector(apiKey, apiSecret)
        
        // Set access token if provided
        // if (accessToken) {
        //   await connector.processRequestToken(accessToken)
        // }
        
        // Use the getUserAssets method from the connector
        const assets = await connector.getUserAssets()
        
        if (Array.isArray(assets) && assets.length > 0) {
          setHoldings(assets)
        } else {
          console.log('No holdings data returned or empty array')
          setHoldings([])
        }
      } catch (error: any) {
        console.error('Error fetching holdings:', error)
        setError(error.message)
      } finally {
        setIsLoading(false)
      }
    }

    fetchHoldings()
  }, [apiKey, apiSecret, accessToken])

  if (isLoading) return <div>Loading holdings...</div>
  if (error) return <div className={styles.error}>{error}</div>
  if (!holdings.length) return <div>No holdings found</div>

  const totalPnL = holdings.reduce((sum, holding) => sum + holding.pnl, 0)
  const totalValue = holdings.reduce(
    (sum, holding) => sum + holding.lastPrice * parseFloat(holding.free),
    0
  )

  return (
    <div className={styles.holdingsContainer}>
      <div className={styles.holdingsSummary}>
        <div className={styles.summaryCard}>
          <h3>Total Portfolio Value</h3>
          <p>₹{totalValue.toLocaleString()}</p>
        </div>
        <div className={styles.summaryCard}>
          <h3>Total P&L</h3>
          <p className={totalPnL >= 0 ? styles.profit : styles.loss}>
            ₹{totalPnL.toLocaleString()}
          </p>
        </div>
      </div>
      <div className={styles.holdingsTable}>
        <table>
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Quantity</th>
              <th>Avg Price</th>
              <th>Current Price</th>
              <th>Current Value</th>
              <th>P&L</th>
              <th>P&L %</th>
            </tr>
          </thead>
          <tbody>
            {holdings.map(holding => {
              const quantity = parseFloat(holding.free)
              const currentValue = holding.lastPrice * quantity
              const pnlPercentage =
                (holding.pnl / (holding.averagePrice * quantity)) * 100
              return (
                <tr key={holding.asset}>
                  <td>{holding.asset}</td>
                  <td>{quantity}</td>
                  <td>₹{holding.averagePrice.toLocaleString()}</td>
                  <td>₹{holding.lastPrice.toLocaleString()}</td>
                  <td>₹{currentValue.toLocaleString()}</td>
                  <td className={holding.pnl >= 0 ? styles.profit : styles.loss}>
                    ₹{holding.pnl.toLocaleString()}
                  </td>
                  <td className={holding.pnl >= 0 ? styles.profit : styles.loss}>
                    {pnlPercentage.toFixed(2)}%
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
